/**
 * Reload-target sentinel: a short-lived sessionStorage marker recording the
 * version we expect to be running after the next hard reload. Lets the
 * watcher detect "we asked to reload to X, did we actually land on X?".
 *
 * Auto-expires after RELOAD_TARGET_TTL_MS so a stuck guard can never block
 * the user indefinitely.
 */

import {
  safeGetSessionStorage,
  safeRemoveSessionStorage,
  safeSetSessionStorage,
} from "@/lib/safe-storage";
import { RELOAD_TARGET_KEY, RELOAD_TARGET_TTL_MS } from "./constants";
import type { StoredReloadTarget } from "./types";

export function writeReloadTarget(target: string): void {
  try {
    const payload: StoredReloadTarget = { t: target, ts: Date.now() };
    safeSetSessionStorage(RELOAD_TARGET_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function readReloadTarget(): { target: string; age: number } | null {
  const raw = safeGetSessionStorage(RELOAD_TARGET_KEY);
  if (!raw) return null;
  try {
    // Backward-compat: plain string from older builds
    if (raw.startsWith("{")) {
      const parsed = JSON.parse(raw) as StoredReloadTarget;
      if (!parsed?.t || typeof parsed.ts !== "number") {
        safeRemoveSessionStorage(RELOAD_TARGET_KEY);
        return null;
      }
      const age = Date.now() - parsed.ts;
      if (age > RELOAD_TARGET_TTL_MS) {
        safeRemoveSessionStorage(RELOAD_TARGET_KEY);
        return null;
      }
      return { target: parsed.t, age };
    }
    // Legacy plain string — treat as expired so we don't block forever.
    safeRemoveSessionStorage(RELOAD_TARGET_KEY);
    return null;
  } catch {
    safeRemoveSessionStorage(RELOAD_TARGET_KEY);
    return null;
  }
}
